import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api, ApiError, uploadRecording } from "../lib/api";
import { formatBytes } from "../lib/format";

type UploadState =
  | { phase: "idle" }
  | { phase: "uploading"; fraction: number; file: File }
  | { phase: "error"; message: string };

export function UploadPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<UploadState>({ phase: "idle" });
  const [dragging, setDragging] = useState(false);

  const readiness = useQuery({ queryKey: ["readiness"], queryFn: api.readiness });

  const startUpload = useCallback(
    async (file: File) => {
      setState({ phase: "uploading", fraction: 0, file });
      try {
        const slot = await api.createUpload(file.name, file.size);
        const session = await uploadRecording(slot.upload_url, file, (fraction) =>
          setState({ phase: "uploading", fraction, file }),
        );
        navigate(`/sessions/${session.id}`);
      } catch (error) {
        // The API reports its own failures in English; only the fallback, which is
        // ours, can be translated.
        const message = error instanceof ApiError ? error.message : t("upload.failed");
        setState({ phase: "error", message });
      }
    },
    [navigate, t],
  );

  const mappingReady = readiness.data?.telemetry_mapping_ready ?? true;

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-semibold tracking-tight">{t("upload.title")}</h1>
      <p className="mt-2 text-sm text-zinc-400">
        {t("upload.introBefore")}
        <code className="rounded bg-zinc-800 px-1 py-0.5 text-xs">
          Le Mans Ultimate/UserData/Telemetry
        </code>
        {t("upload.introAfter")}
      </p>

      {!mappingReady && (
        <div className="mt-6 rounded-lg border border-amber-900/60 bg-amber-950/30 px-4 py-3 text-sm text-amber-200">
          <p className="font-medium">{t("upload.notReadyTitle")}</p>
          <p className="mt-1 text-amber-200/80">
            {t("upload.notReadyBody", {
              fields:
                (readiness.data?.unmapped_fields ?? []).slice(0, 4).join(", ") +
                ((readiness.data?.unmapped_fields.length ?? 0) > 4 ? ", …" : ""),
            })}
          </p>
        </div>
      )}

      <label
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const file = event.dataTransfer.files[0];
          if (file) void startUpload(file);
        }}
        className={[
          "mt-8 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-16 text-center transition-colors",
          dragging
            ? "border-sky-500 bg-sky-500/5"
            : "border-zinc-700 hover:border-zinc-600",
        ].join(" ")}
      >
        <input
          type="file"
          accept=".duckdb"
          className="hidden"
          disabled={state.phase === "uploading"}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void startUpload(file);
          }}
        />
        <span className="text-sm font-medium text-zinc-200">{t("upload.drop")}</span>
        <span className="mt-1 text-xs text-zinc-500">{t("upload.browse")}</span>
      </label>

      {state.phase === "uploading" && (
        <div className="mt-6">
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-zinc-300">{state.file.name}</span>
            <span className="text-zinc-500">
              {formatBytes(state.file.size)} · {Math.round(state.fraction * 100)}%
            </span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-zinc-800">
            <div
              className="h-full rounded-full bg-sky-500 transition-[width]"
              style={{ width: `${state.fraction * 100}%` }}
            />
          </div>
        </div>
      )}

      {state.phase === "error" && (
        <div className="mt-6 rounded-lg border border-red-900/60 bg-red-950/40 px-4 py-3 text-sm text-red-200">
          {state.message}
        </div>
      )}
    </div>
  );
}
